/**
 * Service: relatório de uso do sistema + agrupamento manual de contas por pessoa
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Colaborador, LinhaTempoUso, RelatorioUso } from '../types/usoSistema';

// Tabelas/RPCs novas ainda não estão em integrations/supabase/types.ts
const db = supabase as unknown as SupabaseClient;

interface ColaboradorRow {
  id: string;
  nome: string;
  colaborador_contas: { profile_id: string }[] | null;
}

export const usoSistemaService = {
  async buscarRelatorio(inicio: string, fim: string): Promise<RelatorioUso> {
    const { data, error } = await db.rpc('relatorio_uso_sistema', { p_inicio: inicio, p_fim: fim });
    if (error) throw error;
    return data as RelatorioUso;
  },

  async buscarLinhaTempo(profileIds: string[], dia: string): Promise<LinhaTempoUso> {
    const { data, error } = await db.rpc('relatorio_uso_linha_tempo', {
      p_profile_ids: profileIds,
      p_dia: dia,
    });
    if (error) throw error;
    return data as LinhaTempoUso;
  },

  async listarColaboradores(): Promise<Colaborador[]> {
    const { data, error } = await db
      .from('colaboradores')
      .select('id, nome, colaborador_contas(profile_id)')
      .order('nome');
    if (error) throw error;
    return ((data ?? []) as ColaboradorRow[]).map(c => ({
      id: c.id,
      nome: c.nome,
      profile_ids: (c.colaborador_contas ?? []).map(cc => cc.profile_id),
    }));
  },

  /** Cria a pessoa (se não houver id) e substitui o conjunto de contas vinculadas */
  async salvarColaborador(params: {
    id?: string;
    nome: string;
    tenantId: number;
    profileIds: string[];
  }): Promise<void> {
    let id = params.id;

    if (id) {
      const { error } = await db.from('colaboradores').update({ nome: params.nome }).eq('id', id);
      if (error) throw error;
    } else {
      const { data, error } = await db
        .from('colaboradores')
        .insert({ nome: params.nome, tenant_id: params.tenantId })
        .select('id')
        .single();
      if (error) throw error;
      id = data.id as string;
    }

    const { error: removerError } = await db
      .from('colaborador_contas')
      .delete()
      .eq('colaborador_id', id);
    if (removerError) throw removerError;

    if (params.profileIds.length > 0) {
      // insert simples: se a conta já for de outra pessoa, a PK (profile_id) recusa
      const { error: vincularError } = await db
        .from('colaborador_contas')
        .insert(params.profileIds.map(profile_id => ({ profile_id, colaborador_id: id })));
      if (vincularError?.code === '23505') {
        throw new Error('uma das contas já está vinculada a outra pessoa. Reabra o modal e tente novamente.');
      }
      if (vincularError) throw vincularError;
    }
  },

  async excluirColaborador(id: string): Promise<void> {
    const { error } = await db.from('colaboradores').delete().eq('id', id);
    if (error) throw error;
  },
};
